import {useCallback} from 'react';
import {REACT_APP_MIXPANEL_TOKEN} from '../env';

let mixpanelInstance = null;

/**
 * Dynamically import and initialize Mixpanel
 */
const getMixpanel = async () => {
  if (!mixpanelInstance) {
    try {
      const mixpanelModule = await import('mixpanel-browser');
      const mixpanel = mixpanelModule.default;

      if (!REACT_APP_MIXPANEL_TOKEN) {
        console.warn(
          'Mixpanel token missing. Tracking and identification are disabled.'
        );
        return null;
      }

      if (!mixpanel.__loaded) {
        mixpanel.init(REACT_APP_MIXPANEL_TOKEN, {
          debug: process.env.NODE_ENV !== 'production',
          track_pageview: true,
          persistence: 'localStorage',
          ip: true,
          ignore_dnt: true,
        });
      }

      mixpanelInstance = mixpanel;
    } catch (err) {
      console.error('Error loading Mixpanel:', err);
      mixpanelInstance = null;
    }
  }
  return mixpanelInstance;
};

/**
 * Custom hook for Mixpanel tracking with error handling
 */
const useMixpanel = () => {
  const track = useCallback(async (eventName, properties = {}) => {
    try {
      const mp = await getMixpanel();
      if (mp && mp.__loaded) mp.track(eventName, properties);
    } catch (err) {
      console.error(`Error tracking event "${eventName}":`, err);
    }
  }, []);

  const identify = useCallback(async (userId, traits = {}) => {
    try {
      const mp = await getMixpanel();
      if (mp && mp.__loaded) {
        mp.identify(userId);
        if (Object.keys(traits)?.length) mp.people.set(traits);
      }
    } catch (err) {
      console.error(`Error identifying user "${userId}":`, err);
    }
  }, []);

  const register = useCallback(async (properties, once = false) => {
    try {
      const mp = await getMixpanel();
      if (mp && mp.__loaded) {
        once ? mp.register_once(properties) : mp.register(properties);
      }
    } catch (err) {
      console.error('Error registering properties:', err);
    }
  }, []);

  const reset = useCallback(async () => {
    try {
      const mp = await getMixpanel();
      if (mp && mp.__loaded) mp.reset();
    } catch (err) {
      console.error('Error resetting Mixpanel user:', err);
    }
  }, []);

  const trackPageView = useCallback(async (page, properties = {}) => {
    try {
      const mp = await getMixpanel();
      if (mp && mp.__loaded) mp.track('Page Viewed', { page, ...properties });
    } catch (err) {
      console.error(`Error tracking page view for "${page}":`, err);
    }
  }, []);

  return {
    track,
    identify,
    register,
    reset,
    trackPageView,
  };
};

export default useMixpanel;